import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import store, { State as RootState } from '@/store'

import {Photo} from "@/types/PhotoType";
import {
    deleteItem,
    deleteList,
    fetch,
    moveToFolder,
    recoverDeletedItem,
    recoverDeletedList,
    removeFromFolder,
    sortPhotos,
    uploadPhoto, getUploadUrl, uploadPhotoBySignedUrl, uploadPhotoSuccess, coverPhoto, getProcessedPhotos
} from "@/data/requests/photos";
// import {uploading} from "@/data/requests/analytics";

// Declare state
export type State = {
    photos: Photo[],

    loading: {
        list: boolean,
        delete_item: boolean,
        delete_items_list: boolean,
        recover_delete_item: boolean,
        recover_delete_items_list: boolean
    }
}

// Mutation contracts
export type Mutations<S = State> = {
    setPhotos(state: S, {photos}: {photos: Photo[]}): void,
    setLoadingList(state: S, {status}: {status: boolean}): void
    setLoadingDeleteItem(state: S, {status}: {status: boolean}): void,
    setLoadingDeleteItemsList(state: S, {status}: {status: boolean}): void,
    addPhoto(state: S, {photo}: {photo: Photo, filesize: number}): void,
    addFolder(state: S, {folder, photo}: {folder: string, photo: Photo}): void,
    removeFolder(state: S, {folder, photo}: {folder: string, photo: Photo}): void,
    updateProcessedPhoto(state: S, {photo}: {photo: Photo}): void,
}

// Actions context
type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

// Actions contracts
export interface Actions {
    loadPhotos(
        { commit }: AugmentedActionContext,
        payload: {gallery: string, folder: string | null, cluster: string | number | null, withLoadingStatus: boolean},
    ): Promise<any>,
    deletePhoto(
        { commit }: AugmentedActionContext,
        payload: {photo: Photo},
    ): Promise<any>,
    deleteList(
        { commit }: AugmentedActionContext,
        payload: {photos: Photo[], gallery: string},
    ): Promise<any>,
    sortPhotos(
        { commit }: AugmentedActionContext,
        payload: {photos: Photo[], gallery: string, type: string, folder: string | null},
    ): Promise<any>,
    uploadPhoto(
        { commit }: AugmentedActionContext,
        payload: {file: File, gallery: string, folder: string | null},
    ): Promise<any>,
    moveToFolder(
        { commit }: AugmentedActionContext,
        payload: {photos: Photo[], folder: string},
    ): Promise<any>,
    removeFromFolder(
        { commit }: AugmentedActionContext,
        payload: {photos: Photo[], folder: string},
    ): Promise<any>,
    coverPhoto(
        { commit }: AugmentedActionContext,
        payload: {photo: Photo},
    ): Promise<any>,
    getProcessedPhotos(
        { commit }: AugmentedActionContext,
        payload: {photoIds: number[]},
    ): Promise<any>,
}

// Getters types
export type Getters = {
    photos(state: State): Photo[],
    isLoadingList(state: State): boolean,
    isDeletingItem(state: State): boolean,
    isDeletingItemsList(state: State): boolean,
}

const state: State = {
    photos: [],
    loading: {
        list: false,
        delete_item: false,
        delete_items_list: false,
        recover_delete_item: false,
        recover_delete_items_list: false
    }
};

const getters: GetterTree<State, RootState> & Getters = {
    photos(state: State): Photo[] {
        return state.photos;
    },
    isLoadingList(state: State): boolean {
        return state.loading.list;
    },
    isDeletingItem(state: State): boolean {
        return state.loading.delete_item;
    },
    isDeletingItemsList(state: State): boolean {
        return state.loading.delete_items_list;
    },
    isRecoveringDeletedItem(state: State): boolean {
        return state.loading.recover_delete_item;
    },
    isRecoveringDeletedItemsList(state: State): boolean {
        return state.loading.recover_delete_items_list;
    }
};

const mutations: MutationTree<State> & Mutations = {
    setPhotos(state, {photos}) {
        state.photos = photos;
    },
    setLoadingList(state, {status = false}) {
        state.loading.list = status;
    },
    setLoadingDeleteItem(state, {status}) {
        state.loading.delete_item = status;
    },
    setLoadingDeleteItemsList(state, {status}) {
        state.loading.delete_items_list = status;
    },
    setLoadingRecoverDeletedItem(state, {status}) {
        state.loading.recover_delete_item = status;
    },
    setLoadingRecoverDeletedItemsList(state, {status}) {
        state.loading.recover_delete_items_list = status;
    },
    addPhoto(state, {photo, filesize}) {
        state.photos.unshift(photo);

        store.commit('galleries/addPhotoToGallery', {filesize: filesize});
    },
    addFolder(state, {folder, photo}) {
        const _folder = store.state.galleries.folders.find(item => item.slug == folder);

        if(_folder) {
            const _photo = state.photos.find(item => item.id == photo.id);

            if(_photo) {
                _photo.folders.push(_folder);
            }

            store.commit('galleries/addPhotoToFolder', {folder: folder});
        }
    },
    removeFolder(state, {folder, photo}) {
        const _folder = store.state.galleries.folders.find(item => item.slug == folder);

        if(_folder) {
            const _photo = state.photos.find(item => item.id == photo.id);

            if(_photo && _photo.folders) {
                _photo.folders = _photo.folders.filter(folder => folder.id != _folder.id);
            }

            store.commit('galleries/removePhotoFromFolder', {folder: folder});
        }
    },
    updateProcessedPhoto(state, {photo}) {
        const existingPhotoIndex = state.photos.findIndex(item => item.id === photo.id);
        if (existingPhotoIndex !== -1) {
            // Оновлюємо існуючу фотографію, зберігаючи тимчасові URL для плавного переходу
            const existingPhoto = state.photos[existingPhotoIndex];
            
            // Очищуємо тимчасові URL з пам'яті
            if (existingPhoto.preview_mini_url && existingPhoto.preview_mini_url.startsWith('blob:')) {
                URL.revokeObjectURL(existingPhoto.preview_mini_url);
            }
            if (existingPhoto.compressed_video_url && existingPhoto.compressed_video_url.startsWith('blob:')) {
                URL.revokeObjectURL(existingPhoto.compressed_video_url);
            }
            
            // Оновлюємо фотографію з новими URL з backend
            state.photos[existingPhotoIndex] = photo;
        }
    }
};

const actions: ActionTree<State, RootState> & Actions = {
    loadPhotos({commit}, {gallery, folder = null, cluster = null, withLoadingStatus = true}): Promise<any> {
        if(withLoadingStatus) {
            commit('setLoadingList', {status: true});
        }

        return new Promise((resolve: any, reject) => {
            fetch(gallery, folder, cluster, (photos) => {
                commit('setPhotos', {photos});

                commit('setLoadingList', {status: false});

                resolve(photos);
            }, (type, data) => {
                commit('setLoadingList', {status: false});

                reject({type, data});
            })
        })
    },
    deletePhoto({commit, getters}, {photo}): Promise<any> {
        commit('setLoadingDeleteItem', {status: true});

        return new Promise((resolve: any, reject) => {
            deleteItem(photo.id, () => {
                commit('setLoadingDeleteItem', {status: false});

                commit('setPhotos', {
                    photos: getters.photos.filter((item: Photo) => {
                        return item.id != photo.id;
                    })
                });

                store.commit('galleries/removePhotoFromGallery', {filesize: photo.filesize});

                store.commit('galleries/addDeletedToGallery', {count: 1});

                resolve();
            }, (type, data) => {
                commit('setLoadingDeleteItem', {status: false});

                reject({type, data});
            })
        })
    },
    deleteTrashedPhoto({commit, getters}, {photo}): Promise<any> {
        commit('setLoadingDeleteItem', {status: true});

        return new Promise((resolve: any, reject) => {
            deleteItem(photo.id, () => {
                commit('setLoadingDeleteItem', {status: false});

                commit('setPhotos', {
                    photos: getters.photos.filter((item: Photo) => {
                        return item.id != photo.id;
                    })
                });

                store.commit('galleries/removeDeletedFromGallery', {count: 1});

                resolve();
            }, (type, data) => {
                commit('setLoadingDeleteItem', {status: false});

                reject({type, data});
            })
        })
    },
    deleteList({commit, getters}, {photos, gallery}): Promise<any> {
        commit('setLoadingDeleteItemsList', {status: true});

        return new Promise((resolve: any, reject) => {
            deleteList(photos, gallery, (photos) => {
                commit('setLoadingDeleteItemsList', {status: false});

                let totalFileSize = 0;

                commit('setPhotos', {
                    photos: getters.photos.filter((item: Photo) => {
                        if(!photos.includes(item.id)) {
                            return true;
                        }

                        totalFileSize += item.filesize;

                        return false
                    })
                });

                store.commit('galleries/removePhotoFromGallery', {filesize: totalFileSize, count: photos.length});

                store.commit('galleries/addDeletedToGallery', {count: photos.length});

                resolve();
            }, (type, data) => {
                commit('setLoadingDeleteItemsList', {status: false});

                reject({type, data});
            })
        })
    },
    recoverDeletedPhoto({commit, getters}, {photo}): Promise<any> {
        commit('setLoadingRecoverDeletedItem', {status: true});

        return new Promise((resolve: any, reject) => {
            recoverDeletedItem(photo.id, () => {
                commit('setLoadingRecoverDeletedItem', {status: false});

                commit('setPhotos', {
                    photos: getters.photos.filter((item: Photo) => {
                        return item.id != photo.id;
                    })
                });

                store.commit('galleries/addPhotoToGallery', {filesize: photo.filesize});

                store.commit('galleries/removeDeletedFromGallery', {count: 1});

                resolve();
            }, (type, data) => {
                commit('setLoadingRecoverDeletedItem', {status: false});

                reject({type, data});
            })
        })
    },
    recoverDeletedList({commit, getters}, {photos, gallery}): Promise<any> {
        commit('setLoadingRecoverDeletedItemsList', {status: true});

        return new Promise((resolve: any, reject) => {
            recoverDeletedList(photos, gallery, (photos) => {
                commit('setLoadingRecoverDeletedItemsList', {status: false});

                let totalFileSize = 0;

                commit('setPhotos', {
                    photos: getters.photos.filter((item: Photo) => {
                        if(!photos.includes(item.id)) {
                            return true;
                        }

                        totalFileSize += item.filesize;

                        return false
                    })
                });

                store.commit('galleries/addPhotoToGallery', {filesize: totalFileSize, count: photos.length});

                store.commit('galleries/removeDeletedFromGallery', {count: photos.length});

                resolve();
            }, (type, data) => {
                commit('setLoadingRecoverDeletedItemsList', {status: false});

                reject({type, data});
            })
        })
    },
    sortPhotos({commit, getters}, {photos, gallery, type = "manual", folder = null}): Promise<any> {
        const tempPhotos = getters.photos;

        commit('setPhotos', {photos: (photos.length === 0 ? tempPhotos : photos)});

        return new Promise((resolve: any, reject) => {
            sortPhotos(photos, gallery, type, folder, () => {
                if(folder === null) {
                    store.commit('galleries/setGalleryOrder', {type});
                } else {
                    store.commit('galleries/setFolderOrder', {folder, type});
                }

                resolve();
            }, (type, data) => {
                commit('setPhotos', {photos: tempPhotos});

                reject({type, data});
            })
        })
    },
    async uploadPhoto({commit}, { file, gallery, folder = null }): Promise<any> {
        return await new Promise((resolve: any, reject) => {
            uploadPhoto(file, gallery, folder, (photo) => {
                photo.preview_mini_url = URL.createObjectURL(file);

                commit('addPhoto', {photo, filesize: file.size});

                if(folder) {
                    store.commit('galleries/addPhotoToFolder', {folder: folder});
                }

                resolve(photo);
            }, (type, data) => {
                reject({type, data});
            })
        });
    },
    async uploadPhotoByUrl({commit}, { file, gallery, folder = null, onProgress = null }): Promise<any> {
        return await new Promise((resolve: any, reject) => {
            getUploadUrl({
                name: file.name,
                size: file.size,
                type: file.type,
            }, gallery, folder, (data) => {
                const successUploaded = () => {
                    data.photo.preview_mini_url = URL.createObjectURL(file);

                    if(data.photo.is_video) {
                        data.photo.compressed_video_url = URL.createObjectURL(file);
                    }

                    commit('addPhoto', {photo: data.photo, filesize: file.size});

                    if(folder) {
                        store.commit('galleries/addPhotoToFolder', {folder: folder});
                    }

                    resolve(data.photo);
                }

                uploadPhotoBySignedUrl(file, data.url, (uploadingResponseData: any) => {
                    // uploading(gallery, file.size, file.name, 0, 0, 0, uploadingResponseData);

                    uploadPhotoSuccess(data.photo, () => {
                        successUploaded();
                    }, (type, errorData) => {
                        console.log('Second try: ' + file.name, errorData);

                        uploadPhotoBySignedUrl(file, data.url, () => {
                            uploadPhotoSuccess(data.photo, () => {
                                successUploaded();
                            }, (type, data) => {
                                reject({type, data});
                            });
                        }, (type, data) => {
                            reject({type, data});
                        }, onProgress);
                    });
                }, (type, data) => {
                    reject({type, data});
                }, onProgress);
            }, (type, data) => {
                reject({type, data});
            });
        });
    },
    moveToFolder({commit}, { photos, folder }): Promise<any> {
        return new Promise((resolve: any, reject) => {
            moveToFolder(photos, folder, () => {
                for(let i = 0; i < photos.length; i++) {
                    commit('addFolder', {
                        folder,
                        photo: photos[i]
                    });
                }

                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        });
    },
    removeFromFolder({commit, getters}, { photos, folder}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            removeFromFolder(photos, folder, () => {
                for(let i = 0; i < photos.length; i++) {
                    commit('removeFolder', {
                        folder,
                        photo: photos[i]
                    });
                }

                const removedPhotos = photos.map((item: Photo) => item.id);

                commit('setPhotos', {
                    photos: getters.photos.filter((item: Photo) => {
                        return !removedPhotos.includes(item.id);
                    })
                });

                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        });
    },
    coverPhoto({commit, getters}, {photo}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            coverPhoto(photo, () => {
                commit('setPhotos', {
                    photos: getters.photos.map((item: Photo) => {
                        item.is_cover = item.id === photo.id;
                        return item;
                    })
                });

                resolve();
            }, (type, data) => {
                reject({type, data});
            })
        });
    },
    async getProcessedPhotos({commit}, {photoIds}): Promise<any> {
        return await new Promise((resolve: any, reject) => {
            getProcessedPhotos(photoIds, (photos) => {
                // Оновлюємо кожну оброблену фотографію
                photos.forEach(photo => {
                    commit('updateProcessedPhoto', {photo});
                });
                resolve(photos);
            }, (type, data) => {
                reject({type, data});
            });
        });
    }
};

//setup store type
export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
    > & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const PhotosModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}